'use client';

import { useEffect, useRef } from 'react';
import { reaction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  RingProgress,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure, useInterval } from '@mantine/hooks';
import { fileUploadQueue } from '@/stores/fileUploadQueue';
import { Icon } from '@/ui';

export const BackgroundQueue = observer(() => {
  const { t } = useTranslation();
  const [opened, { toggle }] = useDisclosure(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const interval = useInterval(() => {
    fileUploadQueue.processQueue();
  }, 1000);

  useEffect(() => {
    interval.start();
    return () => interval.stop();
  }, [interval]);

  useEffect(
    () =>
      reaction(
        () => fileUploadQueue.queue.length,
        (length) => {
          if (length > 0) {
            scrollRef.current?.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }
        }
      ),
    []
  );

  if (fileUploadQueue.isEmpty) {
    return null;
  }

  const queue = [...fileUploadQueue.queue].reverse();

  return (
    <Card
      withBorder
      shadow="sm"
      radius="md"
      style={{
        position: 'fixed',
        bottom: 0,
        right: 'var(--mantine-spacing-md)',
        zIndex: 'var(--mantine-z-index-overlay)',
        width: '300px',
      }}
    >
      <Card.Section withBorder inheritPadding py="xs">
        <Group justify="space-between">
          <UnstyledButton onClick={toggle}>
            <Group gap="xs">
              {opened ? <Icon name="chevronDown" /> : <Icon name="chevronUp" />}
              <Text fw={500}>{t('background-queue.upload-queue')}</Text>
            </Group>
          </UnstyledButton>
          <Button variant="subtle" size="xs" onClick={() => fileUploadQueue.clear()}>
            {t('background-queue.clear')}
          </Button>
        </Group>
      </Card.Section>

      {opened && (
        <Card.Section
          inheritPadding
          style={{ maxHeight: '300px', overflow: 'auto' }}
          ref={scrollRef}
        >
          <Box>
            <Stack gap={0}>
              {queue.map((q, index) => (
                <Stack key={`file-${index}`} gap={0}>
                  <Group justify="space-between" py="xs" wrap="nowrap">
                    <Text size="sm" truncate>
                      {q.file.name}
                    </Text>
                    <Center w={30} h={30} style={{ flexShrink: 0 }}>
                      {['pending', 'uploading'].includes(q.status) && (
                        <>
                          {q.progress <= 0 ? (
                            <Loader size="sm" />
                          ) : (
                            <RingProgress
                              sections={[{ value: q.progress, color: 'blue' }]}
                              size={30}
                              thickness={3}
                              roundCaps
                            />
                          )}
                        </>
                      )}
                      {q.status === 'done' && (
                        <Icon name="check" color="green" style={{ width: 20, height: 20 }} />
                      )}
                      {q.status === 'error' && (
                        <Icon name="alertCircle" color="red" style={{ width: 20, height: 20 }} />
                      )}
                    </Center>
                  </Group>
                  {q.status === 'error' && (
                    <Text size="xs" c="red">
                      {t(q.getErrorI18nKey())}
                      {q.errorMessage ? `: ${q.errorMessage}` : ''}
                    </Text>
                  )}
                  {index !== queue.length - 1 && <Divider />}
                </Stack>
              ))}
            </Stack>
          </Box>
        </Card.Section>
      )}
    </Card>
  );
});
